import { c as _c } from "react/compiler-runtime";
import { useId, type ChangeEvent } from "react";
interface TimePickerProps {
  readonly hours: number;
  readonly minutes: number;
  readonly onTimeChange: (hours: number, minutes: number) => void;
  readonly minTime?: {
    hours: number;
    minutes: number;
  } | null;
}
interface TimeOption {
  label: string;
  hours: number;
  minutes: number;
}
const formatTime = (timeHours: number, timeMinutes: number) => {
  const period = timeHours >= 12 ? "PM" : "AM";
  const hour12 = timeHours % 12 === 0 ? 12 : timeHours % 12;
  const minuteLabel = timeMinutes.toString().padStart(2, "0");
  return `${hour12}:${minuteLabel} ${period}`;
};
export default function TimePicker(t0) {
  const $ = _c(75);
  const {
    hours,
    minutes,
    onTimeChange,
    minTime: t1
  } = t0;
  const minTime = t1 === undefined ? null : t1;
  const baseId = useId();
  const hoursInputId = `${baseId}-hours`;
  const minutesInputId = `${baseId}-minutes`;
  const minTimeDescriptionId = minTime ? `${baseId}-min-time` : undefined;
  let t2;
  if ($[0] !== minTime) {
    t2 = minTime === null ? undefined : `Earliest selectable time is ${formatTime(minTime.hours, minTime.minutes)}.`;
    $[0] = minTime;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const minTimeDescription = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[2] !== hours || $[3] !== hoursInputId || $[4] !== minTime || $[5] !== minTimeDescription || $[6] !== minTimeDescriptionId || $[7] !== minutes || $[8] !== minutesInputId || $[9] !== onTimeChange) {
    const timeOptions = [{
      label: "12 AM",
      hours: 0,
      minutes: 0
    }, {
      label: "6 AM",
      hours: 6,
      minutes: 0
    }, {
      label: "9 AM",
      hours: 9,
      minutes: 0
    }, {
      label: "12 PM",
      hours: 12,
      minutes: 0
    }, {
      label: "3 PM",
      hours: 15,
      minutes: 0
    }, {
      label: "6 PM",
      hours: 18,
      minutes: 0
    }, {
      label: "9 PM",
      hours: 21,
      minutes: 0
    }, {
      label: "11:59 PM",
      hours: 23,
      minutes: 59
    }];
    let displayHours;
    let isPm;
    let isTimeDisabled;
    let toggleAmPm;
    if ($[16] !== hours || $[17] !== minTime || $[18] !== minutes || $[19] !== onTimeChange) {
      toggleAmPm = () => {
        const newHours = hours >= 12 ? hours - 12 : hours + 12;
        if (isTimeDisabled(newHours, minutes)) {
          return;
        }
        onTimeChange(newHours, minutes);
      };
      isPm = hours >= 12;
      displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      isTimeDisabled = (h, m) => {
        if (!minTime) {
          return false;
        }
        if (h < minTime.hours) {
          return true;
        }
        if (h === minTime.hours && m < minTime.minutes) {
          return true;
        }
        return false;
      };
      $[16] = hours;
      $[17] = minTime;
      $[18] = minutes;
      $[19] = onTimeChange;
      $[20] = displayHours;
      $[21] = isPm;
      $[22] = isTimeDisabled;
      $[23] = toggleAmPm;
    } else {
      displayHours = $[20];
      isPm = $[21];
      isTimeDisabled = $[22];
      toggleAmPm = $[23];
    }
    let t9;
    if ($[24] !== isPm || $[25] !== isTimeDisabled || $[26] !== minutes || $[27] !== onTimeChange) {
      t9 = e => {
        const val = parseInt(e.target.value, 10);
        if (isNaN(val)) {
          return;
        }
        const newHours_0 = isPm ? val === 12 ? 12 : val + 12 : val === 12 ? 0 : val;
        if (!isTimeDisabled(newHours_0, minutes)) {
          onTimeChange(newHours_0, minutes);
        }
      };
      $[24] = isPm;
      $[25] = isTimeDisabled;
      $[26] = minutes;
      $[27] = onTimeChange;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    const onHoursChange = t9;
    let t10;
    if ($[29] !== hours || $[30] !== isTimeDisabled || $[31] !== onTimeChange) {
      t10 = e_0 => {
        const val_0 = parseInt(e_0.target.value, 10);
        if (isNaN(val_0)) {
          return;
        }
        if (!isTimeDisabled(hours, val_0)) {
          onTimeChange(hours, val_0);
        }
      };
      $[29] = hours;
      $[30] = isTimeDisabled;
      $[31] = onTimeChange;
      $[32] = t10;
    } else {
      t10 = $[32];
    }
    const onMinutesChange = t10;
    t8 = "tw-py-4 tw-relative tw-rounded-lg tw-bg-iron-800/60 tw-shadow-md tw-ring-1 tw-ring-iron-700/50";
    t5 = "tw-px-5";
    let t11;
    if ($[33] !== hoursInputId) {
      t11 = <label className="tw-sr-only" htmlFor={hoursInputId}>Hours</label>;
      $[33] = hoursInputId;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    let t12;
    if ($[35] !== displayHours || $[36] !== hoursInputId || $[37] !== minTimeDescriptionId || $[38] !== onHoursChange) {
      t12 = <input type="number" min="1" max="12" value={displayHours} onChange={onHoursChange} id={hoursInputId} aria-describedby={minTimeDescriptionId} className="tw-w-full tw-bg-[#2A2A33] tw-border-0 tw-text-white tw-rounded-lg tw-p-2 tw-ring-1 tw-ring-iron-700/30 focus:tw-ring-primary-400 focus:tw-outline-none tw-transition-all tw-duration-300 [appearance:textfield] [&::-webkit-outer-spin-button]:tw-appearance-none [&::-webkit-inner-spin-button]:tw-appearance-none" placeholder="HH" />;
      $[35] = displayHours;
      $[36] = hoursInputId;
      $[37] = minTimeDescriptionId;
      $[38] = onHoursChange;
      $[39] = t12;
    } else {
      t12 = $[39];
    }
    let t13;
    if ($[40] !== t11 || $[41] !== t12) {
      t13 = <div className="tw-w-20 tw-relative">{t11}{t12}</div>;
      $[40] = t11;
      $[41] = t12;
      $[42] = t13;
    } else {
      t13 = $[42];
    }
    let t14;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <span className="tw-text-iron-50 tw-font-bold">:</span>;
      $[43] = t14;
    } else {
      t14 = $[43];
    }
    let t15;
    if ($[44] !== minutesInputId) {
      t15 = <label className="tw-sr-only" htmlFor={minutesInputId}>Minutes</label>;
      $[44] = minutesInputId;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    let t16;
    if ($[46] !== minTimeDescriptionId || $[47] !== minutes || $[48] !== minutesInputId || $[49] !== onMinutesChange) {
      t16 = <input type="number" min="0" max="59" value={minutes} onChange={onMinutesChange} id={minutesInputId} aria-describedby={minTimeDescriptionId} className="tw-w-full tw-bg-[#2A2A33] tw-border-0 tw-text-white tw-rounded-lg tw-p-2 tw-ring-1 tw-ring-iron-700/30 focus:tw-ring-primary-400 focus:tw-outline-none tw-transition-all tw-duration-300 [appearance:textfield] [&::-webkit-outer-spin-button]:tw-appearance-none [&::-webkit-inner-spin-button]:tw-appearance-none" placeholder="MM" />;
      $[46] = minTimeDescriptionId;
      $[47] = minutes;
      $[48] = minutesInputId;
      $[49] = onMinutesChange;
      $[50] = t16;
    } else {
      t16 = $[50];
    }
    let t17;
    if ($[51] !== t15 || $[52] !== t16) {
      t17 = <div className="tw-w-20 tw-relative">{t15}{t16}</div>;
      $[51] = t15;
      $[52] = t16;
      $[53] = t17;
    } else {
      t17 = $[53];
    }
    const t18 = isPm ? "PM" : "AM";
    let t19;
    if ($[54] !== t18 || $[55] !== toggleAmPm) {
      t19 = <button onClick={toggleAmPm} aria-label="Toggle AM/PM" className="tw-bg-[#2A2A33] hover:tw-bg-[#32323C] tw-text-white tw-rounded-lg tw-px-3 tw-py-2 tw-transition-all tw-duration-200 tw-border-0 tw-shadow-md hover:tw-shadow-lg hover:tw-translate-y-[-1px] tw-ml-1">{t18}</button>;
      $[54] = t18;
      $[55] = toggleAmPm;
      $[56] = t19;
    } else {
      t19 = $[56];
    }
    if ($[57] !== t13 || $[58] !== t17 || $[59] !== t19) {
      t6 = <div className="tw-flex tw-items-center tw-mb-5"><div className="tw-flex tw-items-center tw-space-x-2 tw-flex-1">{t13}{t14}{t17}{t19}</div></div>;
      $[57] = t13;
      $[58] = t17;
      $[59] = t19;
      $[60] = t6;
    } else {
      t6 = $[60];
    }
    if ($[61] !== minTimeDescription || $[62] !== minTimeDescriptionId) {
      t7 = minTimeDescription ? <p id={minTimeDescriptionId} className="tw-sr-only">{minTimeDescription}</p> : null;
      $[61] = minTimeDescription;
      $[62] = minTimeDescriptionId;
      $[63] = t7;
    } else {
      t7 = $[63];
    }
    t3 = "tw-grid tw-grid-cols-3 tw-gap-1.5";
    t4 = timeOptions.map(option => {
      const disabled = isTimeDisabled(option.hours, option.minutes);
      return <button key={option.label} onClick={() => !disabled && onTimeChange(option.hours, option.minutes)} disabled={disabled} className={`tw-p-1.5 tw-text-xs sm:tw-text-sm tw-rounded-lg tw-transition-all tw-duration-200 tw-border-0 tw-shadow-sm hover:tw-shadow-md hover:tw-translate-y-[-1px] ${hours === option.hours && minutes === option.minutes ? "tw-bg-primary-500 hover:tw-bg-primary-600 tw-text-white tw-ring-2 tw-ring-primary-400/30" : disabled ? "tw-bg-[#2A2A33] tw-text-iron-600 tw-opacity-50 tw-cursor-not-allowed" : "tw-bg-[#2A2A33] tw-text-iron-50 hover:tw-bg-[#32323C]"}`}>{option.label}</button>;
    });
    $[2] = hours;
    $[3] = hoursInputId;
    $[4] = minTime;
    $[5] = minTimeDescription;
    $[6] = minTimeDescriptionId;
    $[7] = minutes;
    $[8] = minutesInputId;
    $[9] = onTimeChange;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[64] !== t3 || $[65] !== t4) {
    t9 = <div className={t3}>{t4}</div>;
    $[64] = t3;
    $[65] = t4;
    $[66] = t9;
  } else {
    t9 = $[66];
  }
  let t10;
  if ($[67] !== t5 || $[68] !== t6 || $[69] !== t7 || $[70] !== t9) {
    t10 = <div className={t5}>{t6}{t7}{t9}</div>;
    $[67] = t5;
    $[68] = t6;
    $[69] = t7;
    $[70] = t9;
    $[71] = t10;
  } else {
    t10 = $[71];
  }
  let t11;
  if ($[72] !== t10 || $[73] !== t8) {
    t11 = <div className={t8}>{t10}</div>;
    $[72] = t10;
    $[73] = t8;
    $[74] = t11;
  } else {
    t11 = $[74];
  }
  return t11;
}